import { darkenHex } from "./tokens";

/**
 * A paleta da marca — as cores fixas da home e das peças de divulgação.
 *
 * ## Por que não mora no `tokens.ts`
 *
 * O `tokens.ts` é o tema do produto: todo token tem par claro/escuro e vira
 * variável CSS que o `.dark` troca. A home não troca de tema — o creme é creme
 * com o sistema em modo escuro. Se ela lesse `hsl(var(--primary-foreground))`,
 * o fundo viraria cinza-escuro para quem usa o app no escuro. Por isso esta
 * paleta é valor fixo, sem esquema (mesma razão do `motion.ts`).
 *
 * Algumas cores coincidem com tokens do tema claro; o teste
 * `brand.unit.test.ts` amarra essas coincidências, para que mudar uma sem a
 * outra seja decisão, não acidente.
 */
/** `bodyGray` cru; escurecido logo abaixo para `mutedBodyGray`. */
const BODY_GRAY = "#848484";

export const BRAND_COLORS = {
  /** Fundo creme da home. = `primary-foreground` do tema claro. */
  offWhite: "#FBF5DF",
  offWhiteHover: "#F5EFD0",
  /** Botão `light`: um passo mais suave que o `offWhiteHover`. */
  offWhiteHoverSoft: "#F7F0D0",
  /** Cinza quase preto dos blocos escuros. = `primary` do tema claro. */
  ink: "#1D1D1D",
  inkHover: "#1A1A1A",
  black: "#000000",
  white: "#FFFFFF",
  bodyGray: BODY_GRAY,
  /**
   * `bodyGray` escurecido 30% — não bate 4.5:1 de contraste contra fundo
   * claro. Um valor só para quem precisa desse tom (a `/links` no web e o
   * `BioLinkRow` no `@tami/ui`), em vez de cada consumidor chamar `darkenHex`
   * por conta própria e divergir se o fator mudar.
   */
  mutedBodyGray: darkenHex(BODY_GRAY, 0.7).toUpperCase(),
  /** = `brand` do tema claro. */
  purple: "#7D4AF9",
  /** = `chart-4` do tema claro. */
  pink: "#F05B7D",
  pinkHover: "#E68BA7",
  /** = `chart-3` do tema claro. */
  orange: "#FA7530",
  /** Verde-limão do marca-texto. = `highlight` do tema claro. */
  green: "#DEF771",
  greenHover: "#D0F95F",
  /** Verde bem claro: degrau inicial dos planos e papel de recado. */
  paleGreen: "#F5FFC9",
  /** Papel dos recados e texto claro sobre fundo escuro. */
  paper: "#FFFDF6",
  /** Creme acinzentado dos stickers. */
  cream: "#F5F1E6",
} as const;

/**
 * Texto secundário de cada papel de recado (`.recado-*` no `home.style.css`).
 * Cada tom é o papel escurecido (ou clareado, nos escuros) até passar o
 * contraste mínimo — por isso um por papel, e não um cinza só.
 */
export const NOTE_SECONDARY = {
  paper: "#5A5A5A",
  green: "#3E4620",
  paleGreen: "#4B5230",
  pink: "#3A0C18",
  orange: "#3A1603",
  purple: "#EBE2FF",
  ink: "#B9B9B4",
} as const;

/**
 * O gradiente da marca — a faixa verde→roxo da Tãmi.
 *
 * O roxo final **não** é o `BRAND_COLORS.purple`: é um tom mais azulado, só do
 * gradiente.
 */
export const BRAND_GRADIENT = [
  { name: "green", color: "#C2F771", stop: 0 },
  { name: "yellow", color: "#D7C759", stop: 14 },
  { name: "orange", color: "#FA7530", stop: 38 },
  { name: "pink", color: "#F05B7D", stop: 62 },
  { name: "purple", color: "#7D5CF2", stop: 100 },
] as const;

/** `brandGradientCss(90)` → `"linear-gradient(90deg, #C2F771 0%, …)"` */
export function brandGradientCss(angleDeg: number): string {
  const stops = BRAND_GRADIENT.map(({ color, stop }) => `${color} ${stop}%`);
  return `linear-gradient(${angleDeg}deg, ${stops.join(", ")})`;
}
